import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable, createComponent } from '@carvana/merch-components';
import { NavPayloadAction } from '@carvana/merch-native';
import DispatcherEvent from '@carvana/merch-native/DispatcherEvent';
import { processUrl } from '@carvana/merch-native/useAnchorClickMessageDispatcher';
import cx from 'classnames';
import { useCombobox } from 'downshift';
import HtmlToReact from 'html-to-react';
import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Consumer } from '~/constants/common/consumer';
import SearchType from '~/constants/common/SearchType';
import { useIsNative } from '~/context/common/DeviceContext';
import type {
  SavedSearch,
  Suggestion,
  SuggestionGroup
} from '~/contracts/common';
import { useTrackRendered } from '~/hooks/common';
import useSearchBarEvents, { EventData } from '~/hooks/home/useSearchBarEvents';
import ClockIcon from '~/icons/Clock';
import CloseIcon from '~/icons/Close';
import HeartWithBorderThin from '~/icons/HeartWithBorderThin';
import SearchIcon from '~/icons/SearchAlt';
import { getSourcedSendMessageToNative } from '~/native/common/dispatchers';
import {
  SearchBarEvents,
  SectionTitles,
  SUBMITTED_INPUT_NO_SELECTION
} from './constants';
import fetchSuggestions from './fetchSuggestions';
import {
  RecentSearchRemoveButton,
  SearchBarInnerWrapper,
  SearchBarWrapper,
  SearchButton,
  SuggestionItem,
  SuggestionsContainerClasses,
  SuggestionTitleWrapper
} from './SearchBar.styles';
import {
  clearRecentSuggestions,
  fetchLatestRecentSuggestions,
  removeSuggestionFromLocalStorage,
  saveSuggestionToLocalStorage
} from './suggestions';
import {
  getHrefWithSource,
  highlightText,
  normalizeSearchInputShape
} from './utils';

const htmlParser = HtmlToReact.Parser();

type ClearAllButtonProps = {
  handleClearAllClick: React.MouseEventHandler<HTMLButtonElement>;
  mobileOnly: boolean;
};

function ClearAllButton({
  mobileOnly,
  handleClearAllClick
}: ClearAllButtonProps) {
  return (
    <button
      id="clear-all-button"
      type="button"
      onClick={handleClearAllClick}
      className={cx(
        'cursor-pointer  bg-transparent p-0 text-blue-2  tl-body-s',
        { 'ml-auto m:hidden ': mobileOnly },
        { 'hidden w-full text-left m:inline': !mobileOnly }
      )}
      data-analytics-id="Search Bar - Clear All"
    >
      Clear All
    </button>
  );
}

function filterSuggestions(
  suggestionsObject: SuggestionGroup,
  inputValue: string
): Suggestion[] {
  if (inputValue === '') {
    return suggestionsObject.suggestions;
  } else {
    return suggestionsObject.suggestions.filter((suggestion: any) =>
      suggestion.text.toLowerCase().includes(inputValue.toLowerCase())
    );
  }
}

const defaultSavedSearches = [];

type OwnProps = {
  background: 'gray' | 'white';
  button: 'small' | 'large';
  savedSearches?: SavedSearch[];
  consumer: Consumer;
};

export default function SearchBar({
  background,
  button,
  savedSearches: savedSearchesFromProps = defaultSavedSearches,
  consumer
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const isNative = useIsNative();

  const getHref = useMemo(() => getHrefWithSource(consumer), [consumer]);
  const sendMessageToNative = useMemo(
    () => getSourcedSendMessageToNative(consumer),
    [consumer]
  );

  const searchBarEvents = useSearchBarEvents(trackEvent);

  const [suggestedSearches, setSuggestedSearches] = useState<SuggestionGroup>({
    title: SectionTitles.KEYWORD_SUGGESTIONS_TITLE,
    suggestions: [],
    icon: SearchIcon
  });

  const [savedSearches, setSavedSearches] = useState<SuggestionGroup>({
    title: SectionTitles.SAVED_SEARCHES_TITLE,
    suggestions: [],
    icon: createComponent(HeartWithBorderThin, {
      className: 'h-16 w-16 text-grey-5'
    })
  });

  const [recentSearches, setRecentSearches] = useState<SuggestionGroup>({
    title: SectionTitles.RECENT_SEARCHES_TITLE,
    suggestions: [],
    icon: ClockIcon
  });

  const [dropDownOptions, setDropDownOptions] = useState<SuggestionGroup[]>([]);
  const dropDownOptionsSuggestionsArray = dropDownOptions.reduce<Suggestion[]>(
    (acc, group) => {
      acc.push(...group.suggestions);

      return acc;
    },
    []
  );

  const dropDownOptionsCount = dropDownOptions.reduce(
    (acc, item) => acc + item.suggestions.length,
    0
  );

  const [inputValue, setInputValue] = useState('');
  const hasInput = inputValue.length > 0;

  const [inputFocused, setInputFocused] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const noHighlightedIndex = highlightedIndex < 0;

  const highlightedSuggestion =
    dropDownOptionsSuggestionsArray[highlightedIndex];

  const shouldFire = consumer === Consumer.HOME && inputFocused;
  useTrackRendered('srch-home-text-search-default', shouldFire);

  useMemo(() => {
    setDropDownOptions([
      { ...savedSearches, suggestions: savedSearches.suggestions.slice(0, 3) },
      {
        ...recentSearches,
        suggestions: recentSearches.suggestions.slice(0, 10)
      },
      { ...suggestedSearches }
    ]);
  }, [savedSearches, recentSearches, suggestedSearches]);

  useEffect(() => {
    setSavedSearches(prevState => ({
      ...prevState,
      suggestions: normalizeSearchInputShape({
        searchInputType: SearchType.SavedSearch,
        data: savedSearchesFromProps.reverse()
      })
    }));
  }, [savedSearchesFromProps]);

  useEffect(() => {
    setRecentSearches(prevState => ({
      ...prevState,
      suggestions: fetchLatestRecentSuggestions()
    }));
  }, []);

  const handleInputOnFocused: React.FocusEventHandler<HTMLInputElement> = e => {
    e.preventDefault();

    searchBarEvents.focused();
    setInputFocused(true);
  };

  const handleInputOnChange: React.ChangeEventHandler<
    HTMLInputElement
  > = async e => {
    const newInputValue = e.currentTarget.value;
    setInputValue(newInputValue);

    const keywordSuggestions = await fetchSuggestions(newInputValue);

    if (keywordSuggestions.length === 0) {
      setNoResults(true);
      searchBarEvents.noResults(newInputValue);
    } else {
      setNoResults(false);
    }

    setDropDownOptions(
      dropDownOptions.map(item => {
        switch (item.title) {
          case SectionTitles.SAVED_SEARCHES_TITLE:
            item.suggestions = filterSuggestions(
              savedSearches,
              newInputValue
            ).slice(0, 3);
            break;

          case SectionTitles.RECENT_SEARCHES_TITLE:
            const recentSearchLimit = hasInput && !noResults ? 3 : 10;
            item.suggestions = filterSuggestions(
              recentSearches,
              newInputValue
            ).slice(0, recentSearchLimit);
            break;

          case SectionTitles.KEYWORD_SUGGESTIONS_TITLE:
            const updatedSuggestedSearches = {
              ...suggestedSearches,
              suggestions: keywordSuggestions
            };
            item.suggestions = updatedSuggestedSearches.suggestions;
            break;

          default:
            break;
        }

        item.suggestions.map(
          suggestion =>
            (suggestion.highlightedText = highlightText(
              suggestion.text,
              newInputValue
            ))
        );

        return item;
      })
    );
  };

  // Clears all highlighted matches in order to refresh the UI
  const resetHighlightedText = (suggestionsObject: SuggestionGroup) => {
    suggestionsObject.suggestions.map(
      suggestion => (suggestion.highlightedText = suggestion.text)
    );
  };

  const handleClearTextClick: React.MouseEventHandler<
    HTMLButtonElement
  > = e => {
    e.preventDefault();
    setInputValue('');

    resetHighlightedText(savedSearches);
    resetHighlightedText(recentSearches);

    // Clear Suggestions list
    setSuggestedSearches(prevState => ({
      ...prevState,
      suggestions: []
    }));

    // Reset the recent searches
    setRecentSearches(prevState => ({
      ...prevState,
      suggestions: fetchLatestRecentSuggestions()
    }));
  };

  const handleRemoveRecentSearch: React.MouseEventHandler<
    HTMLButtonElement
  > = e => {
    e.preventDefault();
    e.stopPropagation();
    searchBarEvents.removeRecentSearch();

    const updatedRecentSearches = removeSuggestionFromLocalStorage(
      highlightedSuggestion,
      inputValue
    );

    setRecentSearches(prevState => ({
      ...prevState,
      suggestions: updatedRecentSearches
    }));
  };

  const handleClearAllClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    clearRecentSuggestions();
    searchBarEvents.clearAllRecentSearches();

    setRecentSearches(prevState => ({
      ...prevState,
      suggestions: []
    }));
  };

  const handleNavigation = (url: string, eventData: EventData) => {
    if (isNative) {
      const { url: nativeUrl, urlPayload } = processUrl(url);

      sendMessageToNative(DispatcherEvent.LeaveFullscreen, {});

      sendMessageToNative(DispatcherEvent.Link, {
        linkType: 'browser',
        action: NavPayloadAction.ViewSrp,
        url: `${process.env.CVNA_APP_ABSOLUTE_BASE_URL}${nativeUrl}`,
        ...urlPayload
      });
    } else {
      window.location.href = getHref(url, eventData);
    }
  };

  const handleGoButtonClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    // Use the first option if no selection is highlighted
    const suggestion =
      highlightedSuggestion ||
      dropDownOptionsSuggestionsArray[0] ||
      SUBMITTED_INPUT_NO_SELECTION;

    const { text, searchType, href } = suggestion;
    const eventData = {
      buildKey: 'buildKey' in suggestion ? Number(suggestion.buildKey) : null,
      textSource: searchType,
      suggestionIndex: hasInput
        ? noHighlightedIndex
          ? 1
          : highlightedIndex + 1
        : 0,
      suggestionsLength: dropDownOptions.length,
      suggestionText: text,
      textEntered: inputValue
    };

    searchBarEvents.goClicked(eventData);

    let destinationHref: string;

    if (inputFocused && hasInput) {
      destinationHref = href;

      setInputValue(text);
      setInputFocused(false);

      // Save Suggested Search to Local Storage Only if Focused
      if (searchType === SearchType.TextSearch) {
        saveSuggestionToLocalStorage(
          highlightedSuggestion || dropDownOptionsSuggestionsArray[0]
        );
      }
    } else {
      destinationHref = '/cars';
    }

    handleNavigation(destinationHref, eventData);
  };

  const handleSuggestionClick: React.MouseEventHandler = e => {
    e.preventDefault();

    if (!highlightedSuggestion) return;

    const suggestion = highlightedSuggestion;
    const { text, searchType, href } = suggestion;

    // Save Suggested Search to Local Storage
    if (searchType === SearchType.TextSearch) {
      saveSuggestionToLocalStorage(highlightedSuggestion);
    }

    const eventData = {
      buildKey: 'buildKey' in suggestion ? Number(suggestion.buildKey) : 0,
      textSource: searchType,
      suggestionIndex: highlightedIndex + 1,
      suggestionsLength: dropDownOptions.length,
      suggestionText: text,
      textEntered: inputValue
    };

    searchBarEvents.suggestionClicked(eventData);

    const url = getHref(href, eventData);

    handleNavigation(url, eventData);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Backspace':
        if (inputValue.length <= 1) {
          setSavedSearches(prevState => ({
            ...prevState
          }));
          setRecentSearches(prevState => ({
            ...prevState
          }));
          setSuggestedSearches(prevState => ({
            ...prevState,
            suggestions: []
          }));
          resetHighlightedText(recentSearches);
          resetHighlightedText(savedSearches);

          return;
        }
        break;
      case 'Enter':
        // Use the first option if no selection is highlighted
        const suggestion =
          highlightedSuggestion ||
          dropDownOptionsSuggestionsArray[0] ||
          SUBMITTED_INPUT_NO_SELECTION;

        const { text, searchType, href } = suggestion;

        if (hasInput && !searchType) return;

        const eventData = {
          buildKey: 'buildKey' in suggestion ? Number(suggestion.buildKey) : 0,
          textSource: searchType,
          suggestionIndex:
            !hasInput && noHighlightedIndex ? 0 : highlightedIndex + 1,
          suggestionsLength: dropDownOptions.length,
          suggestionText: text,
          textEntered: inputValue
        };
        searchBarEvents.pressedEnter(eventData);

        if (!hasInput && noHighlightedIndex) {
          window.location.href = getHref('/cars', eventData);

          return;
        }

        setInputValue(text);

        // Save Suggested Search to Local Storage
        if (searchType === SearchType.TextSearch) {
          saveSuggestionToLocalStorage(
            highlightedSuggestion || dropDownOptionsSuggestionsArray[0]
          );
        }

        window.location.href = getHref(href, eventData);

        return;

      case 'ArrowUp':
        if (highlightedIndex === -1) return;
        setHighlightedIndex(highlightedIndex - 1);
        break;

      case 'ArrowDown':
        if (highlightedIndex === dropDownOptionsCount - 1) return;
        setHighlightedIndex(highlightedIndex + 1);
        break;

      default:
        break;
    }
  };

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    getToggleButtonProps
  } = useCombobox({
    id: 'common-search-bar',
    items: dropDownOptions
  });

  return (
    <form
      className="relative z-[1] w-full"
      data-qa="search-bar-container"
      action="."
    >
      <SearchBarWrapper
        className={cx(
          background === 'gray' ? 'bg-grey-1' : 'bg-white',
          (isOpen && dropDownOptionsCount > 0) || (noResults && isOpen)
            ? 'rounded-t-[8px]'
            : 'rounded-[8px]'
        )}
      >
        <SearchBarInnerWrapper>
          <div className="flex pl-12 pr-8">
            <SearchIcon />
          </div>
          <input
            type="search"
            className="h-full flex-1 border-none bg-transparent outline-none"
            placeholder="Search Make, Model, or Keyword"
            {...getInputProps({
              value: inputValue,
              onFocus: handleInputOnFocused,
              onChange: handleInputOnChange,
              onKeyDown: handleKeyDown
            })}
          />

          {isOpen && hasInput ? (
            <button
              type="button"
              aria-label="close menu"
              title="Close Search Menu"
              {...getToggleButtonProps({
                className: 'flex items-center bg-transparent fill-blue-6',
                onClick: handleClearTextClick
              })}
              data-analytics-id={SearchBarEvents.ClearedText}
            >
              <CloseIcon />
            </button>
          ) : null}
        </SearchBarInnerWrapper>

        <div className="p-[6px]">
          <SearchButton
            type="button"
            className={button === 'large' ? 'h-44 w-48' : undefined}
            disabled={noResults && hasInput}
            onClick={handleGoButtonClick}
            data-analytics-id="Search Bar - Go"
          >
            GO
          </SearchButton>
        </div>
      </SearchBarWrapper>

      {isOpen && noResults && hasInput ? (
        <ul {...getMenuProps()} className={`${SuggestionsContainerClasses}`}>
          <div className="flex cursor-pointer items-center">
            <SuggestionItem>
              No results for &quot;{inputValue.toLowerCase()}&quot;
            </SuggestionItem>
          </div>
        </ul>
      ) : (
        <ul
          {...getMenuProps({
            onMouseLeave: () => setHighlightedIndex(-1)
          })}
          className={`${
            isOpen && dropDownOptionsCount > 0
              ? `${SuggestionsContainerClasses}`
              : 'hidden'
          }`}
        >
          {
            dropDownOptions.reduce<{
              sections: JSX.Element[];
              itemIndex: 0;
            }>(
              (acc, section, sectionIdx) => {
                const { title, suggestions, icon: Icon } = section;

                if (suggestions.length === 0) {
                  return acc;
                }

                acc.sections.push(
                  <section key={sectionIdx} className="pb-36 last:pb-0">
                    <SuggestionTitleWrapper>
                      {Icon && <Icon />}
                      {title}
                      {title === SectionTitles.RECENT_SEARCHES_TITLE && (
                        <ClearAllButton
                          mobileOnly
                          handleClearAllClick={handleClearAllClick}
                        />
                      )}
                    </SuggestionTitleWrapper>

                    {suggestions.map(
                      (suggestion: Suggestion, itemIdx: number) => {
                        const idx = acc.itemIndex++;

                        return (
                          <Clickable
                            key={itemIdx}
                            className="flex cursor-pointer items-center"
                            onMouseEnter={() => setHighlightedIndex(idx)}
                            onTouchStart={() => setHighlightedIndex(idx)}
                            onClick={handleSuggestionClick}
                            style={{
                              backgroundColor:
                                highlightedIndex === idx
                                  ? 'var(--cvna-color-grey-2)'
                                  : 'var(--cvna-color-white)'
                            }}
                            data-analytics-id="Search Bar - Suggestion"
                          >
                            <SuggestionItem
                              {...getItemProps({
                                // @ts-ignore TODO: Why is this working?
                                item: suggestion.text
                              })}
                              title={suggestion.text}
                            >
                              {htmlParser.parse(suggestion.highlightedText)}
                            </SuggestionItem>
                            {title === SectionTitles.RECENT_SEARCHES_TITLE && (
                              <RecentSearchRemoveButton
                                type="button"
                                onClick={handleRemoveRecentSearch}
                                data-analytics-id="Search Bar - Remove Recent Search"
                              >
                                <CloseIcon height="10" width="10" />
                              </RecentSearchRemoveButton>
                            )}
                          </Clickable>
                        );
                      }
                    )}

                    {title === SectionTitles.RECENT_SEARCHES_TITLE && (
                      <ClearAllButton
                        mobileOnly={false}
                        handleClearAllClick={handleClearAllClick}
                      />
                    )}
                  </section>
                );

                return acc;
              },
              {
                sections: [],
                itemIndex: 0
              }
            ).sections
          }
        </ul>
      )}
    </form>
  );
}
